import { useId } from "react";
import { cn } from "@/lib/utils";
import ConfirmDelete from "@/components/molecules/confirm-delete";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";

interface RadioShippingProps {
  items: {
    value: string;
    content: { title?: string; description?: string; postalCode?: string; city?: string };
    onDelete?: () => void;
    onEdit?: () => void;
    deleteModalOpen?: boolean;
    onDeleteModalChange?: (open: boolean) => void;
  }[];
  defaultValue?: string;
  onValueChange?: ((value: string) => void) | undefined;
  name?: string;
  isPending?: boolean;
  className?: string;
  align?: "left" | "center";
  hasBorder?: boolean;
  locale: Locales;
}

const RadioShipping = ({
  items,
  name = "shipping-address",
  defaultValue,
  onValueChange,
  className,
  locale,
  align = "left",
  hasBorder = false,
  isPending,
}: RadioShippingProps) => {
  const id = useId();

  return (
    <div className={cn("flex max-w-[350px] flex-col gap-2", className)}>
      {items.map(
        (
          {
            value,
            content: { title, description, postalCode, city },
            onDelete,
            onEdit,
            deleteModalOpen,
            onDeleteModalChange,
          },
          index
        ) => (
          <fieldset
            key={index}
            className={cn(
              "has-checked:border-blue-brand [&_input:checked~div_h2]:text-blue-brand has-checked:bg-primary/0 w-full rounded-md border-2 border-transparent bg-[#F4F1F380] outline-none [&_input:checked~div_h2]:font-bold"
            )}
          >
            <legend className="sr-only">Shipping Addresses</legend>
            <label
              htmlFor={`${id}-${index}`}
              className={cn(
                "flex w-full cursor-pointer items-center gap-3 rounded-md p-4",
                align === "center" && "justify-center",
                hasBorder && "border-primary-light/30 border"
              )}
            >
              <input
                id={`${id}-${index}`}
                type="radio"
                name={name}
                value={value}
                checked={defaultValue === value}
                onChange={(e) => onValueChange && onValueChange(e.target.value)}
                className="border-ring checked:bg-blue-brand checked:border-blue-brand focus:ring-blue-brand/50 relative mt-0.5 size-5 cursor-pointer appearance-none rounded-full border-2 bg-white transition-all before:absolute before:top-1/2 before:left-1/2 before:h-2 before:w-2 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-white before:opacity-0 before:content-[''] checked:before:opacity-100 focus:ring-2 focus:outline-none"
              />
              <div className={cn("flex flex-col gap-3", align === "left" && "grow")}>
                <div className="flex items-center justify-between">
                  <h2 className={cn("text-dark-primary font-cabinet-grotesk text-lg leading-none font-medium")}>
                    {title}
                  </h2>
                  <div className="text-primary flex items-center gap-2.5 text-base">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={onEdit}
                        className="cursor-pointer underline underline-offset-2 outline-offset-2 transition-all duration-300 ease-in-out hover:scale-105"
                      >
                        {__("Edit", locale)}
                      </button>
                    )}
                    {onDelete && (
                      <ConfirmDelete
                        open={deleteModalOpen}
                        onOpenChange={onDeleteModalChange}
                        isActionHappening={isPending}
                        locale={locale}
                        title={__("Delete address", locale)}
                        text={__("Are you sure you want to delete this address?", locale)}
                        onClick={onDelete}
                        modalTrigger={
                          <button
                            type="button"
                            className="cursor-pointer underline underline-offset-2 outline-offset-2 transition-all duration-300 ease-in-out hover:scale-105"
                          >
                            {__("Delete", locale)}
                          </button>
                        }
                      />
                    )}
                  </div>
                </div>
                {description && (
                  <p>
                    {description}
                    <br />
                    {postalCode}
                    <br />
                    {city}
                  </p>
                )}
              </div>
            </label>
          </fieldset>
        )
      )}
    </div>
  );
};

export default RadioShipping;
